import { API_BASE, authHeaders } from './apiAuth'

/**
 * What the platform is spending
 * -------------------------------------------------------------
 * One request answers the whole page, aggregated inside Postgres. Nothing here
 * adds anything up, and the raw events — every address, every project touched
 * — never leave the database.
 */

export type UsageGrain = 'day' | 'month' | 'year'

/** Amazon requests this billing period, as Rainforest counts them. */
export interface UsageAmazon {
  plan: string
  used: number
  remaining: number | null
  limit: number | null
  resetsAt: string
  extra: number
  extraUsd: number
}

function shapeAmazon(value: unknown): UsageAmazon | null {
  if (!value || typeof value !== 'object') return null
  const a = value as Record<string, unknown>
  const n = (v: unknown) => Number(v) || 0
  return {
    plan: typeof a.plan === 'string' ? a.plan : '',
    used: n(a.used),
    remaining: a.remaining == null ? null : n(a.remaining),
    limit: a.limit == null ? null : n(a.limit),
    resetsAt: typeof a.resetsAt === 'string' ? a.resetsAt : '',
    extra: n(a.extra),
    extraUsd: n(a.extraUsd),
  }
}

/**
 * Amazon's count on its own (2026-10-05): a call out to Rainforest, the
 * slowest thing the page used to wait for. `closed` says a period's extra
 * has just been written into the bill, so the bills are worth reading again.
 */
export async function readUsageAmazon(): Promise<{ amazon: UsageAmazon | null; closed: boolean }> {
  const response = await fetch(`${API_BASE}/api/usage/amazon`, {
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (!response.ok) throw new Error(`Could not read Amazon's count (HTTP ${response.status})`)
  const body = (await response.json()) as { amazon?: unknown; closed?: unknown }
  return { amazon: shapeAmazon(body.amazon), closed: body.closed === true }
}

export interface UsageTotals {
  cost: number
  calls: number
  people: number
  failedCost: number
  failedCalls: number
  inputTokens: number
  cachedTokens: number
  outputTokens: number
}

export interface UsagePoint {
  at: string
  cost: number
  calls: number
}

export interface UsageModelPoint {
  at: string
  model: string
  cost: number
}

export interface UsagePerson {
  email: string
  name: string
  cost: number
  calls: number
  lastAt: string
}

export interface UsageSlice {
  email: string
  /** A day for `personDays`, a model slug for `personModels`. */
  key: string
  cost: number
}

/** Everything spent in one stretch of time: by day, model and person. */
export interface UsageWindow {
  totals: { cost: number; calls: number; people: number }
  amazonRequests: number
  days: { at: string; cost: number; calls: number }[]
  dayModels: { at: string; model: string; cost: number }[]
  people: UsagePerson[]
  personModels: { email: string; model: string; cost: number }[]
  personDayModels: { email: string; at: string; model: string; cost: number }[]
}

/** One line of a month's bill (docs/usage-window-and-bills.sql). */
export interface BillLine {
  month: string
  item: string
  kind: 'models' | 'subscription' | 'amazon'
  usd: number
  detail: string
}

export interface UsageReport {
  grain: UsageGrain
  totals: UsageTotals
  /** This calendar month so far, which is what a monthly budget is read against. */
  month: { cost: number; calls: number }
  overall: UsagePoint[]
  byModel: UsageModelPoint[]
  people: UsagePerson[]
  /**
   * This month's spend by person, New York's month (docs/usage-new-york.sql,
   * 2026-11-04), for the person cards. Null from a database without it.
   */
  monthPeople: UsagePerson[] | null
  personDays: UsageSlice[]
  personModels: UsageSlice[]
  /**
   * A person's month by day AND model, for splitting their daily chart by
   * family. Empty from a database that has not run usage-person-by-model.sql,
   * and the page then draws the plain white line it always did.
   */
  personDayModels: { email: string; at: string; model: string; cost: number }[]
  /** Null when nobody has set one. Not zero — that would mean "spend nothing". */
  budget: number | null
  /**
   * This month's subscriptions (workers/api/src/subscriptions.ts): written
   * into its bill on its first day. Empty before `subscriptionsFrom`.
   */
  subscriptions: { name: string; usd: number; note: string }[]
  /** The first month subscriptions count in, as YYYY-MM-01. */
  subscriptionsFrom: string
  /**
   * Amazon requests this billing period, read from Rainforest. Null when it
   * could not be asked -- and, since 2026-10-05, always null here: it comes
   * on its own from readUsageAmazon, so the page never waits for it.
   */
  amazon: UsageAmazon | null
  /** The last 30 days, for the person cards. Null before the SQL file is run. */
  last30: UsageWindow | null
  /** Every recorded month's bill, newest first. */
  bills: BillLine[]
  /** The month still running, as YYYY-MM-01. */
  thisMonth: string
}

const num = (value: unknown): number => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}
const str = (value: unknown): string => (typeof value === 'string' ? value : '')

function rows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? (value.filter((r) => r && typeof r === 'object') as Record<string, unknown>[]) : []
}

/**
 * The model slug as the Usage page counts it. Every Upscale model is one line,
 * "topaz" (Huanrui, 2026-10-06: which Topaz model was used is too fine a
 * split to be worth a row). The database keeps the real slugs.
 */
const modelSlug = (value: unknown): string => {
  const slug = str(value)
  return slug.startsWith('topaz-') ? 'topaz' : slug
}

/** Rows that now share a key, after modelSlug, added together. */
function folded<T extends { cost: number }>(list: T[], key: (row: T) => string): T[] {
  const out = new Map<string, T>()
  for (const row of list) {
    const k = key(row)
    const held = out.get(k)
    if (held) held.cost += row.cost
    else out.set(k, { ...row })
  }
  return [...out.values()]
}

/**
 * Read the answer defensively.
 *
 * Postgres hands `numeric` back as a JSON number, but it is one column type
 * change away from arriving as a string, and a chart plotted from `"1.40" - 0`
 * is not an error, it is a flat line. Everything is put through Number here so
 * that cannot happen quietly.
 */
export function shapeWindow(raw: unknown): UsageWindow | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const totals = (r.totals ?? {}) as Record<string, unknown>
  return {
    totals: { cost: num(totals.cost), calls: num(totals.calls), people: num(totals.people) },
    amazonRequests: num(r.amazonRequests),
    days: rows(r.days).map((d) => ({ at: str(d.at), cost: num(d.cost), calls: num(d.calls) })),
    dayModels: folded(
      rows(r.dayModels).map((d) => ({ at: str(d.at), model: modelSlug(d.model), cost: num(d.cost) })),
      (d) => `${d.at}|${d.model}`,
    ),
    people: rows(r.people).map((p) => ({
      email: str(p.email),
      name: str(p.name) || str(p.email),
      cost: num(p.cost),
      calls: num(p.calls),
      lastAt: str(p.last_at),
    })),
    personModels: folded(
      rows(r.personModels).map((p) => ({ email: str(p.email), model: modelSlug(p.model), cost: num(p.cost) })),
      (p) => `${p.email}|${p.model}`,
    ),
    personDayModels: folded(
      rows(r.personDayModels).map((p) => ({
        email: str(p.email),
        at: str(p.at),
        model: modelSlug(p.model),
        cost: num(p.cost),
      })),
      (p) => `${p.email}|${p.at}|${p.model}`,
    ),
  }
}

function bills(raw: unknown): BillLine[] {
  return rows(raw).map((b) => ({
    month: str(b.month).slice(0, 10),
    item: str(b.item),
    kind: (['models', 'subscription', 'amazon'] as const).find((k) => k === b.kind) ?? 'subscription',
    usd: num(b.usd),
    detail: str(b.detail),
  }))
}

function shape(raw: Record<string, unknown>): UsageReport {
  const totals = (raw.totals ?? {}) as Record<string, unknown>
  const month = (raw.month ?? {}) as Record<string, unknown>
  return {
    grain: (['day', 'month', 'year'] as const).find((g) => g === raw.grain) ?? 'day',
    totals: {
      cost: num(totals.cost),
      calls: num(totals.calls),
      people: num(totals.people),
      failedCost: num(totals.failedCost),
      failedCalls: num(totals.failedCalls),
      inputTokens: num(totals.inputTokens),
      cachedTokens: num(totals.cachedTokens),
      outputTokens: num(totals.outputTokens),
    },
    month: { cost: num(month.cost), calls: num(month.calls) },
    overall: rows(raw.overall).map((r) => ({ at: str(r.at), cost: num(r.cost), calls: num(r.calls) })),
    byModel: folded(
      rows(raw.byModel).map((r) => ({ at: str(r.at), model: modelSlug(r.model), cost: num(r.cost) })),
      (r) => `${r.at}|${r.model}`,
    ),
    people: rows(raw.people).map((r) => ({
      email: str(r.email),
      name: str(r.name) || str(r.email),
      cost: num(r.cost),
      calls: num(r.calls),
      lastAt: str(r.last_at),
    })),
    monthPeople: Array.isArray(raw.monthPeople)
      ? rows(raw.monthPeople).map((r) => ({
          email: str(r.email),
          name: str(r.name) || str(r.email),
          cost: num(r.cost),
          calls: num(r.calls),
          lastAt: str(r.last_at),
        }))
      : null,
    personDays: rows(raw.personDays).map((r) => ({ email: str(r.email), key: str(r.at), cost: num(r.cost) })),
    personModels: folded(
      rows(raw.personModels).map((r) => ({ email: str(r.email), key: modelSlug(r.model), cost: num(r.cost) })),
      (r) => `${r.email}|${r.key}`,
    ),
    personDayModels: folded(
      rows(raw.personDayModels).map((r) => ({
        email: str(r.email),
        at: str(r.at),
        model: modelSlug(r.model),
        cost: num(r.cost),
      })),
      (r) => `${r.email}|${r.at}|${r.model}`,
    ),
    budget: raw.budget == null ? null : num(raw.budget),
    subscriptions: rows(raw.subscriptions).map((r) => ({ name: str(r.name), usd: num(r.usd), note: str(r.note) })),
    subscriptionsFrom: str(raw.subscriptionsFrom),
    amazon: shapeAmazon(raw.amazon),
    last30: shapeWindow(raw.last30),
    bills: bills(raw.bills),
    thisMonth: str(raw.thisMonth),
  }
}

export async function readUsage(grain: UsageGrain): Promise<UsageReport> {
  const response = await fetch(`${API_BASE}/api/usage?grain=${grain}`, {
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (response.status === 403) throw new Error('Administrators only')
  if (!response.ok) throw new Error(`Could not read the usage (HTTP ${response.status})`)
  return shape((await response.json()) as Record<string, unknown>)
}

/** One month in full, for a bill opened from the list. */
export async function readUsageMonth(month: string): Promise<{ window: UsageWindow | null; bill: BillLine[] }> {
  const response = await fetch(`${API_BASE}/api/usage/month?month=${encodeURIComponent(month.slice(0, 7))}`, {
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (!response.ok) throw new Error(`Could not read that month (HTTP ${response.status})`)
  const body = (await response.json()) as Record<string, unknown>
  return { window: shapeWindow(body.window), bill: bills(body.bill) }
}

/** Everything spent since a moment, for a person opened by month or by year. */
export async function readUsageWindow(since: string): Promise<UsageWindow | null> {
  const response = await fetch(`${API_BASE}/api/usage/window?since=${encodeURIComponent(since)}`, {
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (!response.ok) throw new Error(`Could not read that span (HTTP ${response.status})`)
  return shapeWindow(((await response.json()) as Record<string, unknown>).window)
}

/* -------------------------------------------------------------------------
   Storage (2026-10-05): what Supabase and R2 hold, for the Usage page.
   ------------------------------------------------------------------------- */

export interface StoragePart {
  name: string
  bytes: number
  objects: number
}

export interface StorageSide {
  /** When it was counted; null before the first count. */
  at: string | null
  bytes: number
  objects: number
  /** One point a day, oldest first. */
  history: { day: string; bytes: number }[]
}

export interface StorageReport {
  supabase: StorageSide & { database: number; parts: StoragePart[] }
  r2: StorageSide & { parts: StoragePart[] }
  /** What each plan includes before it charges, in bytes. */
  allowances: { supabaseFiles: number; supabaseDatabase: number; r2: number }
  /** The figures are an older count, being counted again: worth asking once more shortly. */
  recounting: boolean
}

function parts(value: unknown): StoragePart[] {
  if (!Array.isArray(value)) return []
  return value.map((one: Record<string, unknown>) => ({
    name: String(one.name ?? ''),
    bytes: Number(one.bytes) || 0,
    objects: Number(one.objects) || 0,
  }))
}

function side(value: Record<string, unknown> | undefined): StorageSide {
  return {
    at: typeof value?.at === 'string' ? value.at : null,
    bytes: Number(value?.bytes) || 0,
    objects: Number(value?.objects) || 0,
    history: Array.isArray(value?.history)
      ? (value!.history as Record<string, unknown>[]).map((one) => ({
          day: String(one.day ?? ''),
          bytes: Number(one.bytes) || 0,
        }))
      : [],
  }
}

/** Administrators only; counted at most once an hour by the Worker. */
export async function readStorage(): Promise<StorageReport> {
  const response = await fetch(`${API_BASE}/api/storage`, {
    headers: { Accept: 'application/json', ...(await authHeaders('web')) },
  })
  if (response.status === 403) throw new Error('Administrators only')
  if (!response.ok) throw new Error(`Could not read the storage (HTTP ${response.status})`)
  const body = (await response.json()) as Record<string, Record<string, unknown>>
  const supabaseDetail = (body.supabase?.detail ?? {}) as Record<string, unknown>
  const r2Detail = (body.r2?.detail ?? {}) as Record<string, unknown>
  const allow = body.allowances ?? {}
  return {
    supabase: { ...side(body.supabase), database: Number(supabaseDetail.database) || 0, parts: parts(supabaseDetail.buckets) },
    r2: { ...side(body.r2), parts: parts(r2Detail.parts) },
    allowances: {
      supabaseFiles: Number(allow.supabaseFiles) || 0,
      supabaseDatabase: Number(allow.supabaseDatabase) || 0,
      r2: Number(allow.r2) || 0,
    },
    recounting: (body as Record<string, unknown>).recounting === true,
  }
}
